import React, { useEffect } from "react";
import H4 from "../../components/Typography/H4";
import { useFetchApi } from "../../hooks/useFetchApi";
import TextFieldWithLabel from "../../components/TextFieldWithLabel";
import Loader from "../../components/Loader";
import TextField from "../../components/TextField";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import SortableCategory from "./SortableCategory";
import DotVRIcon from "../../icons/DotVRIcon";
import Badge from "../../components/Badge";
import SortableGraphData from "./SortableGraphData";

interface GraphEditorProps {
  name?: string;
}

const GraphEditor: React.FC<GraphEditorProps> = ({ name }) => {
  const [categorySearchKey, setCategorySearchKey] = React.useState<string>("");
  const {
    response: graphCategories,
    loading: graphCategoriesLoading,
    execute: getGraphCategories,
  } = useFetchApi("/api/graph-categories", {}, "GET");

  const { execute: updateOrder } = useFetchApi(
    "/api/graph-categories-order",
    {},
    "POST",
    getGraphCategories
  );
  const { execute: updateSnapshotOrder } = useFetchApi(
    "/api/snapshot-order",
    {},
    "POST",
    getGraphCategories
  );
  const { execute: updateSnapshot } = useFetchApi(
    "/api/update-snapshot",
    {},
    "POST",
    getGraphCategories
  );
  const { execute: deleteSnapshot } = useFetchApi(
    "/api/delete-snapshot",
    {},
    "POST",
    getGraphCategories
  );

  const [selectedCategory, setSelectedCategory] = React.useState<any>(
    (graphCategories && graphCategories[0].id) || null
  );
  const [selectedSnapshots, setSelectedSnapshots] = React.useState<any>([]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  const filterByName = (category: any) => {
    if (!categorySearchKey) return category;
    if (category.name.toLowerCase().includes(categorySearchKey.toLowerCase())) {
      return category;
    }
    return false;
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    // if a snapshot was moved to a category then update the category_id of the snapshot
    if (active.data.current.snapshot && over.data.current.category) {
      const { snapshot } = active.data.current;
      const { category } = over.data.current;
      let order_no = 1;
      // check the snapshot array inside the category and update the order_no of the snapshot
      if (category.snapshots.length > 0) {
        category.snapshots.forEach((snapshot: any) => {
          if (snapshot.order_no > order_no) {
            order_no = snapshot.order_no;
          }
        });
        order_no++;
      }

      if (snapshot.category_id !== category.id) {
        updateSnapshotOrder({
          snapshots: [{ id: snapshot.id, order_no, category_id: category.id }],
        });
      }
    }

    // if a snapshot was moved to a snapshot then update the order_no of the snapshot
    if (active.data.current.snapshot && over.data.current.snapshot) {
      const activeOrderId = active.data.current.snapshot.order_no;
      const overOrderId = over.data.current.snapshot.order_no;
      if (active.data.current.snapshot.id !== over.id) {
        const activeCategory = graphCategories.filter(
          (category: any) => category.id === selectedCategory
        )[0];
        updateSnapshotOrder({
          snapshots: activeCategory.snapshots.map((snapshot: any) => {
            if (snapshot.id === active.data.current.snapshot.id) {
              snapshot.order_no = overOrderId;
            }
            if (snapshot.id === over.data.current.snapshot.id) {
              snapshot.order_no = activeOrderId;
            }
            return {
              order_no: snapshot.order_no,
              id: snapshot.id,
              category_id: selectedCategory,
            };
          }),
        });
      }
    }

    // if a category was moved to a category then update the order_no of the category
    if (active.data.current.category && over.data.current.category) {
      const activeOrderId = active.data.current.category.order_no;
      const overOrderId = over.data.current.category.order_no;
      if (active.data.current.category.id !== over.id) {
        updateOrder({
          categories: graphCategories.map((cat: any) => {
            if (cat.id === active.data.current.category.id) {
              cat.order_no = overOrderId;
            }
            if (cat.id === over.data.current.category.id) {
              cat.order_no = activeOrderId;
            }
            return { order_no: cat.order_no, id: cat.id };
          }),
        });
      }
    }
  };

  useEffect(() => {
    getGraphCategories();
  }, []);

  const handleDelete = (graph_name: string) => {
    deleteSnapshot({ graph_name });
  };

  const handleGraphNameUpdate = (graph_id: number, graph_name: string) => {
    updateSnapshot({ graph_id, graph_name });
  };

  useEffect(() => {
    if (selectedCategory) {
      const snapshots = graphCategories.filter(
        (category: any) => category.id === selectedCategory
      )[0].snapshots;
      // loop the snapshots and convert the graph_data to json object
      const parsedSnapshots = snapshots.map((snapshot: any) => {
        return {
          ...snapshot,
          graph_data: snapshot.graph_data
            ? JSON.parse(snapshot.graph_data)
            : undefined,
        };
      });
      setSelectedSnapshots(parsedSnapshots);
    }
  }, [selectedCategory, graphCategories]);

  return (
    <div className="flex min-h-full h-full flex-col p-8 bg-white">
      <Loader show={graphCategoriesLoading} />
      <div className="flex justify-between mb-8 text-white">
        <H4>Graph Editor</H4>
      </div>
      <div className="flex h-full w-full">
        <div className="flex h-full flex-col w-full bg-gray-200 px-4 py-3">
          <TextField
            className="my-2"
            full
            placeholder="Search Category"
            id="category-search"
            value={categorySearchKey}
            onChange={(e: any) => setCategorySearchKey(e.target.value)}
          />
          <div className="flex h-full flex-col w-full overflow-y-auto">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={[
                  ...(graphCategories && graphCategories.length
                    ? graphCategories
                    : []),
                  ...(selectedSnapshots && selectedSnapshots.length
                    ? selectedSnapshots
                    : []),
                ]}
                strategy={verticalListSortingStrategy}
              >
                {graphCategories &&
                  graphCategories.filter(filterByName).map((category: any) => {
                    return (
                      <>
                        <SortableCategory
                          category={category}
                          handleClick={setSelectedCategory}
                          selected={selectedCategory}
                          selectedSnapshots={selectedSnapshots}
                        />
                        {category.id === selectedCategory && (
                          <div className="flex flex-col w-full bg-gray-300 pl-8">
                            {selectedSnapshots &&
                              selectedSnapshots.map((snapshot: any) => {
                                return (
                                  <SortableGraphData
                                    snapshot={snapshot}
                                    onDelete={handleDelete}
                                    onUpdate={handleGraphNameUpdate}
                                  />
                                );
                              })}
                          </div>
                        )}
                      </>
                    );
                  })}
              </SortableContext>
            </DndContext>
          </div>
        </div>
        {/* <div className="w-9/12 h-full bg-gray-300">
                    <DndContext 
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        // onDragEnd={handleGraphDataDragEnd}
                        >
                            <SortableContext items={selectedSnapshots && selectedSnapshots.length ? selectedSnapshots: []} strategy={verticalListSortingStrategy}>
                        {
                            selectedSnapshots && selectedSnapshots.map((snapshot: any) => {
                                return (
                                    <SortableGraphData snapshot={snapshot} />
                                )
                            })
                        }
                        </SortableContext>
                    </DndContext>
                </div> */}
      </div>
    </div>
  );
};

export default GraphEditor;
